/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { ConfirmModal } from '@/components/ui'
import { insightName } from '@/insights/insightCatalogue'

import { APP_GRANT_LABELS, type AppGrant } from './types'
import type { UsageCount, UsageIndex } from './usage'

interface Props {
  disabled: { grants: AppGrant[]; insights: string[] } | null
  usage: UsageIndex
  /** When given, each line carries its usage ("Reefer Fuel (In use by 3 users, 4 dashboards)"). */
  inUseLabel?: (count: UsageCount) => string
  isSaving?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/** Asks before a save switches off something that is on a dashboard right now. */
export const ConfirmDisableModal: React.FC<Props> = ({
  disabled,
  usage,
  inUseLabel,
  isSaving,
  onConfirm,
  onCancel,
}) => {
  // The shared Modal keeps its body mounted while closed; only mount when there is a question.
  if (disabled === null) return null
  const lines = [
    ...(disabled?.grants ?? []).map((grant) => ({ key: grant, name: APP_GRANT_LABELS[grant] })),
    ...(disabled?.insights ?? []).map((slug) => ({ key: slug, name: insightName(slug) })),
  ]
  return (
    <ConfirmModal
      show
      title="Disable permissions in use?"
      variation="warning"
      confirmLabel="Disable and save"
      cancelLabel="Keep editing"
      isLoading={isSaving}
      onConfirm={onConfirm}
      onCancel={onCancel}
      body={
        <div data-testid="confirm-disable">
          <p className="mb-2 text-sm">The following are currently in use on a dashboard and will stop working:</p>
          <ul className="list-disc pl-5 text-sm">
            {lines.map((line) => (
              <li key={line.key}>
                {line.name}
                {inUseLabel ? ` (${inUseLabel(usage.forSlug(line.key))})` : ''}
              </li>
            ))}
          </ul>
        </div>
      }
    />
  )
}

export default ConfirmDisableModal
